import React, { useEffect, useState } from "react";
import { api } from "../api";
import { toast } from "./Toast";

// LegalGate — hard gate on the vendor's legal terms (Terms of Service, Privacy
// Policy, Data Processing Agreement). A firm's advisor must accept the current
// version of every required document before using the product; each acceptance
// is recorded immutably server-side (who/version/when/IP/UA). Re-prompts when a
// document version bumps.
//
// Wraps the advisor app: renders `children` only once the gate is satisfied.
// For non-advisors it's a pass-through (the /legal endpoints are advisor-only).

type Doc = { key: string; title: string; version: string; effective_date: string; summary: string; body: string };

export default function LegalGate({ user, children }: { user: { role?: string } | null; children: React.ReactNode }) {
  const isAdvisor = user?.role === "advisor";
  const [ready, setReady] = useState(!isAdvisor);       // non-advisors pass through immediately
  const [satisfied, setSatisfied] = useState(false);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [draftNotice, setDraftNotice] = useState<string>("");
  const [isReprompt, setIsReprompt] = useState(false);
  const [agreed, setAgreed] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAdvisor) return;
    let alive = true;
    Promise.all([api.get("/legal/status"), api.get("/legal/documents")])
      .then(([st, dd]: any[]) => {
        if (!alive) return;
        setSatisfied(!!st?.gate_satisfied);
        setDocs(dd?.documents || []);
        setDraftNotice(dd?.draft_notice || st?.draft_notice || "");
        setIsReprompt(!!st?.is_reprompt);
        setReady(true);
      })
      .catch(() => { if (alive) { setSatisfied(true); setReady(true); } }); // fail-open so a transient error never locks the advisor out
    return () => { alive = false; };
  }, [isAdvisor]);

  const allAgreed = docs.length > 0 && docs.every(d => agreed[d.key]);

  // Open one document's full text in its own browser tab — keeps the gate a
  // light acknowledgement instead of a wall of inline legal text. Content is our
  // own trusted LEGAL_DOCUMENTS, but we escape first, then apply light markdown.
  const openDoc = (d: Doc) => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to open the document in a new tab."); return; }
    const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const html = esc(d.body).split("\n").map(ln => {
      if (/^# /.test(ln)) return `<h1>${ln.slice(2)}</h1>`;
      if (ln.trim() === "") return "";
      const t = ln.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      if (/^- /.test(ln)) return `<p class="li">• ${t.slice(2)}</p>`;
      if (/^_.*_$/.test(ln.trim())) return `<p class="fine">${t.replace(/^_/, "").replace(/_$/, "")}</p>`;
      return `<p>${t}</p>`;
    }).join("");
    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8">` +
      `<meta name="viewport" content="width=device-width,initial-scale=1">` +
      `<title>${esc(d.title)} — v${esc(d.version)}</title><style>` +
      `body{margin:0;background:#F5F3EE;color:#1f2937;font:15px/1.65 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif}` +
      `.wrap{max-width:760px;margin:0 auto;padding:44px 24px 90px}` +
      `.meta{font-size:12px;color:#9CA3AF;text-transform:uppercase;letter-spacing:.06em;margin-bottom:4px}` +
      `h1{font-size:25px;font-weight:800;letter-spacing:-.02em;margin:.15em 0 .7em}` +
      `p{margin:.55em 0}.li{margin:.25em 0 .25em 6px}.fine{color:#6B7280;font-size:13px;margin-top:1.4em}` +
      `strong{font-weight:700;color:#111827}</style></head><body><div class="wrap">` +
      `<div class="meta">v${esc(d.version)} · ${esc(d.effective_date)}</div>${html}</div></body></html>`
    );
    w.document.close();
  };

  const acceptAll = async () => {
    setSubmitting(true);
    try {
      const st: any = await api.post("/legal/accept", {});
      if (st?.gate_satisfied) { setSatisfied(true); toast.success("Thanks — recorded."); }
      else toast.error("Something's still unaccepted — please try again.");
    } catch (e: any) {
      toast.error(e?.message || "Couldn't record your acceptance. Try again.");
    } finally { setSubmitting(false); }
  };

  if (!ready) {
    return <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280", fontSize: "14px" }}>Loading…</div>;
  }
  if (!isAdvisor || satisfied) return <>{children}</>;

  // ── The blocking gate ──────────────────────────────────────────────────
  return (
    <div style={{ position: "fixed", inset: 0, background: "#F5F3EE", zIndex: 4000, overflowY: "auto", padding: "40px 20px" }}>
      <div style={{ maxWidth: "760px", margin: "0 auto" }}>
        <div style={{ marginBottom: "18px" }}>
          <div style={{ fontSize: "12px", fontWeight: 800, color: "var(--brand-primary)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" }}>
            {isReprompt ? "Quick update" : "Before you begin"}
          </div>
          <h1 style={{ margin: "0 0 8px", fontSize: "24px", fontWeight: 800, color: "#111827", letterSpacing: "-0.02em" }}>
            {isReprompt ? "We've recently updated our terms of service" : "Review & accept the terms"}
          </h1>
          <p style={{ margin: 0, fontSize: "13.5px", color: "#6B7280", lineHeight: 1.55 }}>
            {isReprompt
              ? "Nothing you need to do differently — just confirm you've seen what changed. Open each to read the full text in its own tab, then check the box."
              : "To use the platform you need to accept the agreements below. Open each to read the full text in its own tab, then check the box."} Your acceptance is recorded with a timestamp for both of our records.
          </p>
        </div>

        {draftNotice && (
          <div style={{ background: "#FEF6EC", border: "1px solid #FADCB2", borderRadius: "6px", padding: "10px 14px", marginBottom: "18px", fontSize: "12px", color: "#92400E", lineHeight: 1.5 }}>
            ⚠️ {draftNotice}
          </div>
        )}

        <div style={{ display: "grid", gap: "10px" }}>
          {docs.map(d => (
            <div key={d.key} style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "14px 18px", display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: "11px", cursor: "pointer", flex: 1, minWidth: "260px" }}>
                <input type="checkbox" checked={!!agreed[d.key]} onChange={e => setAgreed(a => ({ ...a, [d.key]: e.target.checked }))} style={{ marginTop: "3px", flexShrink: 0 }} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "14.5px", fontWeight: 700, color: "#111827" }}>I have read and agree to the {d.title}</span>
                    <span style={{ fontSize: "11px", color: "#9CA3AF", whiteSpace: "nowrap" }}>v{d.version}</span>
                  </span>
                  <span style={{ display: "block", fontSize: "12.5px", color: "#6B7280", lineHeight: 1.5, marginTop: "3px" }}>{d.summary}</span>
                </span>
              </label>
              <button onClick={() => openDoc(d)} style={{ padding: "8px 14px", borderRadius: "6px", border: "1px solid #E5E7EB", background: "#FCFCFB", color: "#374151", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}>
                Read full text ↗
              </button>
            </div>
          ))}
        </div>

        <div style={{ marginTop: "22px", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "14px" }}>
          <span style={{ fontSize: "12px", color: allAgreed ? "#6B7280" : "#B45309" }}>
            {allAgreed ? "Ready." : "Check each agreement to continue."}
          </span>
          <button
            onClick={acceptAll}
            disabled={!allAgreed || submitting}
            style={{
              padding: "11px 22px", borderRadius: "6px", border: "none", fontSize: "14px", fontWeight: 700, fontFamily: "inherit",
              background: "var(--brand-primary)", color: "white",
              cursor: !allAgreed || submitting ? "not-allowed" : "pointer", opacity: !allAgreed || submitting ? 0.5 : 1,
            }}
          >{submitting ? "Recording…" : "Accept & continue"}</button>
        </div>
      </div>
    </div>
  );
}
